import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Exam } from '../../types';
import { getAllExams, toggleExamArchive } from '../../services/examService';

const Container = styled.div`
  min-height: 100vh;
  background: #1e3a8a;
  padding: 20px;
`;

const Header = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const BackButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #475569;
  border: none;
  cursor: pointer;
  font-weight: 600;
  margin-bottom: 16px;

  &:hover {
    background: #e2e8f0;
  }
`;

const ExamList = styled.div`
  display: grid;
  gap: 16px;
`;

const ExamCard = styled.div<{ $archived: boolean }>`
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid ${props => props.$archived ? '#fbbf24' : '#e2e8f0'};
  border-left: 4px solid ${props => props.$archived ? '#f59e0b' : '#3b82f6'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: ${props => props.$archived ? 0.7 : 1};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ExamInfo = styled.div`
  flex: 1;
`;

const ExamName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatusBadge = styled.span<{ $archived: boolean }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$archived
    ? '#fef3c7'
    : '#dbeafe'};
  color: ${props => props.$archived
    ? '#92400e'
    : '#1e40af'};
`;

const ExamDetails = styled.div`
  font-size: 14px;
  color: #64748b;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const ArchiveButton = styled.button<{ $archived: boolean }>`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: ${props => props.$archived
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'};
  color: white;
  border: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: white;
  font-size: 18px;
`;

const ExamArchiveList: React.FC = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadExams();
  }, []);

  const loadExams = async () => {
    try {
      setLoading(true);
      const allExams = await getAllExams();
      setExams(allExams);
    } catch (error) {
      console.error('Error loading exams:', error);
      alert('İmtahanlar yüklənərkən xəta baş verdi');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleArchive = async (examId: string, currentStatus: boolean) => {
    try {
      setToggling(prev => ({ ...prev, [examId]: true }));
      const result = await toggleExamArchive(examId, !currentStatus);

      if (result.success) {
        await loadExams();
        alert(result.message);
      } else {
        alert(result.message);
      }
    } catch (error) {
      alert('Xəta baş verdi');
    } finally {
      setToggling(prev => ({ ...prev, [examId]: false }));
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Yüklənir...</LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <BackButton onClick={() => navigate('/admin/dashboard')}>
        ← Geri
      </BackButton>

      <Header>
        <Title>İmtahan Arxivi</Title>
      </Header>

      <ExamList>
        {exams.map((exam) => (
          <ExamCard key={exam.id} $archived={exam.archived || false}>
            <ExamInfo>
              <ExamName>
                {exam.name}
                <StatusBadge $archived={exam.archived || false}>
                  {exam.archived ? '📦 Arxivləşib' : '✨ Aktiv'}
                </StatusBadge>
              </ExamName>
              <ExamDetails>
                <span>Növ: {exam.type}</span>
                <span>Collection: {exam.collection}</span>
                {exam.archived && exam.archivedAt && (
                  <span>
                    Arxivləşib: {new Date(exam.archivedAt.seconds * 1000).toLocaleDateString('az-AZ')}
                  </span>
                )}
              </ExamDetails>
            </ExamInfo>
            <ArchiveButton
              $archived={exam.archived || false}
              onClick={() => handleToggleArchive(exam.id, exam.archived || false)}
              disabled={toggling[exam.id]}
            >
              {toggling[exam.id]
                ? 'Gözləyin...'
                : exam.archived
                  ? '🔄 Arxivdən Çıxar'
                  : '📦 Arxivləşdir'}
            </ArchiveButton>
          </ExamCard>
        ))}
      </ExamList>
    </Container>
  );
};

export default ExamArchiveList;
